import React, {useEffect, useRef, useState} from 'react';
import Modal from '../Modals/Modal';
import toast from 'react-hot-toast';

const AddProjectModal = ({
  isOpen,
  onClose,
  workspaceId,
  workspaceName,
  userId,
  existingProjects, 
  onAddSuccess,    
}) => {
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [newProjectCodePrefix, setNewProjectCodePrefix] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [projectSuggestions, setProjectSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [showAllProjects, setShowAllProjects] = useState(false);
  const inputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setNewProjectName('');
      setNewProjectDescription('');
      setNewProjectCodePrefix('');
      setError(null);
      setProjectSuggestions([]);
      setShowSuggestions(false);
      setSuggestionIndex(0);
      setShowAllProjects(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Update suggestions based on input
  useEffect(() => {
    if (!isOpen) return;
    let suggestions = [];
    if (newProjectName.trim()) {
      const query = newProjectName.toLowerCase().trim();
      suggestions = existingProjects.filter(project =>
        project.name?.toLowerCase().includes(query)
      );
    } else if (showAllProjects) {
      suggestions = [...existingProjects];
    }
    suggestions.sort((a, b) => a.name?.localeCompare(b.name));
    setProjectSuggestions(suggestions.slice(0, 10));
    if (
      (showAllProjects && existingProjects.length > 0) ||
      (newProjectName.trim() && suggestions.length > 0)
    ) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    setSuggestionIndex(0);
  }, [newProjectName, existingProjects, showAllProjects, isOpen]);

  // Auto-fill description when name changes (if description is empty or equals previous name)
  useEffect(() => {
    if (
      newProjectName &&
      (!newProjectDescription || newProjectDescription === newProjectName)
    ) {
      setNewProjectDescription(newProjectName);
    }
  }, [newProjectName]);

  const handleSuggestionClick = (project) => {
    setNewProjectName(project.name);
    setNewProjectDescription(project.description || project.name);
    setShowSuggestions(false);
    setShowAllProjects(false);
  };

  const handleProjectNameKeyDown = (e) => {
    if (!showSuggestions) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSuggestionIndex((prev) =>
          prev < projectSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSuggestionIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (projectSuggestions[suggestionIndex]) {
          handleSuggestionClick(projectSuggestions[suggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setShowAllProjects(false);
        break;
      default:
        break;
    }
  };

  const handleInputFocus = () => {
    if (existingProjects.length > 0 && !newProjectName.trim()) {
      setShowAllProjects(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      setShowAllProjects(false);
    }, 200);
  };

  const handleInputChange = (e) => {
    setNewProjectName(e.target.value);
    if (e.target.value.trim()) {
      setShowAllProjects(false);
    }
  };

  const handleAddProject = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const finalDescription = newProjectDescription.trim() || newProjectName;
      const response = await fetch(`${API_URL}/api/Project/AddProject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newProjectName,
          description: finalDescription,
          workspaceId: workspaceId,
          userId: userId,
          codePrefix: newProjectCodePrefix || null,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to add project');
      }
      toast.success('Project Added Successfully');
      if (onAddSuccess) await onAddSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
      toast.error(err.message || 'Failed to add project');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Ctrl+S shortcut
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === 's' &&
        isOpen &&
        newProjectName &&
        !isSubmitting
      ) {
        event.preventDefault();
        handleAddProject();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, newProjectName, isSubmitting]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Project" disableClose={isSubmitting} style={{ zIndex: 999999 }}>
      <div className="settings-form">
        <div className="form-group">
          <h4>Workspace: {workspaceName}</h4>
        </div>
        <div className="form-group">
          <label>Project Name:</label>
          <div className="autocomplete-container">
            <input
              ref={inputRef}
              type="text"
              value={newProjectName}
              onChange={handleInputChange}
              onKeyDown={handleProjectNameKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="Type project name or click to see existing projects"
              autoFocus
              disabled={isSubmitting}
            />
            {showSuggestions && (
              <div className="autocomplete-suggestions">
                {projectSuggestions.map((project, index) => (
                  <div
                    key={project.id}
                    className={`suggestion-item ${
                      index === suggestionIndex ? 'suggestion-active' : ''
                    }`}
                    onMouseEnter={() => setSuggestionIndex(index)}
                    onMouseDown={() => handleSuggestionClick(project)}
                  >
                    <div className="suggestion-name">
                      {project.name}
                      {project.status !== 1 && (
                        <span className="suggestion-status"> (Inactive)</span>
                      )}
                    </div>
                  </div>
                ))}
                {projectSuggestions.length === 0 && (
                  <div className="suggestion-item no-suggestions">
                    <div className="suggestion-name">No projects found</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="form-group">
          <label>Project Description:</label>
          <textarea
            value={newProjectDescription}
            onChange={(e) => setNewProjectDescription(e.target.value)}
            placeholder="Enter project description"
            disabled={isSubmitting}
          />
        </div>
        <div className="form-group">
          <label>Code Prefix:</label>
          <input
            type="text"
            value={newProjectCodePrefix}
            onChange={(e) => setNewProjectCodePrefix(e.target.value)}
            placeholder="Enter code prefix (e.g., PROJ)"
            disabled={isSubmitting}
          />
        </div>
        {error && (
          <div className="form-error">
            <i className="fas fa-exclamation-circle"></i> {error}
          </div>
        )}
        <div className="modal-footer">
          <button
            className="btn-primary"
            onClick={handleAddProject}
            disabled={!newProjectName || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Adding...
              </>
            ) : (
              'OK'
            )}
          </button>
          <button className="btn-close" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddProjectModal;